import {Component, DestroyRef, Inject, Input, OnInit, DOCUMENT} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {NgSelectModule} from '@ng-select/ng-select';
import {LookupService} from '../../../common/service/lookup.service';
import {MapToolsService} from '../../services/map-tools.service';
import {WidgetService} from '../../services/widget.service';
import {MetaData, NgEventBus} from 'ng-event-bus';
import {MapService} from '../../services/map.service';
import {ReferenceScaleLookup} from '../../../common/models/reference-scale.model';

@Component({
    selector: 'nlr-zoom-scale',
    imports: [CommonModule, FormsModule, NgSelectModule],
    providers: [CommonModule],
    templateUrl: './zoom-scale.component.html',
    styleUrls: ['./zoom-scale.component.scss']
})
export class ZoomScaleComponent implements OnInit {
    @Input()
    disabled: boolean;

    title: string = 'ZoomScaleComponent';

    referenceScaleFormatted = '1:';

    referenceScales: string[] = [];

    referenceScaleSearchText = null;

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private destroyRef: DestroyRef,
        private eventBus: NgEventBus,
        private lookupService: LookupService,
        private mapService: MapService,
        private mapToolsService: MapToolsService,
        private widgetService: WidgetService
    ) {}

    ngOnInit() {
        this.referenceScales = this.lookupService
            .getLookups('referenceScale')
            .sort((l1: ReferenceScaleLookup, l2: ReferenceScaleLookup) => (l1.uiOptionOrder > l2.uiOptionOrder ? 1 : -1))
            .map((l: ReferenceScaleLookup) => l.referenceScale);

        this.mapToolsService.init(
            'ZOOM',
            () => this.mapService.enableZoomInToExtent(),
            () => this.mapService.deactivateNavToolbar(),
            () => this.zoomToPlanExtentDisabled()
        );

        this.mapToolsService.init(
            'ZOOM_TO_PLAN_EXTENT',
            () => this.mapService.moveToPlanExtent(),
            null,
            () => this.zoomToPlanExtentDisabled()
        );

        this.eventBus
            .on('zoomPlanExtent')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.zoomToPlanExtent();
            });

        this.eventBus
            .on('scaleMapEvent')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((meta: MetaData) => {
                const scale = parseInt(meta.data);
                if (!isNaN(scale)) {
                    this.referenceScaleFormatted = '1:' + scale.toFixed(0);
                }
            });

        this.eventBus
            .on('ZoomInToExtentFinished')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                console.debug(`${this.title} - ZoomInToExtentFinished`);
                this.mapService.enableZoomInToExtent();
            });
    }

    getZoomTooltip() {
        return this.mapToolsService.getTooltipWithShortcut('ZOOM', 'Zoom by rectangle selection');
    }

    getZoomToPlanTooltip() {
        return this.mapToolsService.getTooltipWithShortcut('ZOOM_TO_PLAN_EXTENT', 'Zoom to plan');
    }

    isZoomInToExtentActive() {
        return !this.mapToolsService.isInactive('ZOOM');
    }

    isZoomInToExtentSuspended() {
        return (
            this.mapToolsService.isSuspended('ZOOM') &&
            (!this.mapToolsService.isActive('PAN') || this.mapToolsService.getActiveToolsThatSuspend('ZOOM').length > 1)
        );
    }

    zoomInToExtentDisabled() {
        return this.widgetService.isWidgetDisabled('ZoomInToExtent');
    }

    zoomToPlanExtentDisabled() {
        return this.widgetService.disableAllWidgets;
    }

    zoomToPlanExtent() {
        console.debug(`${this.title} - zoomToPlanExtent`);
        this.mapToolsService.activate('ZOOM_TO_PLAN_EXTENT').subscribe();
    }

    toggleZoomInToExtent() {
        console.debug(`${this.title} - toggleZoomInToExtent`);
        this.mapToolsService.toggle('ZOOM');
    }

    onSelectReferenceScale(scale: string) {
        console.debug(`${this.title} - referenceScaleChanged`);

        // Remove whitespace and '1:' prefix if present
        scale = scale && scale.replace(/\s+/g, '').replace('1:', '');

        if (scale && scale.match(/^\d{1,8}$/)) {
            this.referenceScaleFormatted = `1:${scale}`;
            this.mapService.setMapScale(parseInt(scale));
        } else {
            this.referenceScaleFormatted = `1:${this.mapService.getMapScale().toFixed(0)}`;
        }
    }

    getReferenceScales() {
        if (!this.referenceScaleSearchText) {
            return this.referenceScales;
        }
        const newScales = this.referenceScales.filter(referenceScale => referenceScale.includes(this.referenceScaleSearchText));
        if (newScales.indexOf(this.referenceScaleSearchText) === -1) {
            newScales.unshift(this.referenceScaleSearchText);
        }
        return newScales;
    }

    referenceScaleTrackBy(referenceScale) {
        return referenceScale;
    }

    onSelectReferenceScaleOpen() {
        this.referenceScaleSearchText = null;

        window.setTimeout(() => {
            const mapScale = Math.round(this.mapService.getMapScale());
            const divisors = this.referenceScales.map(v => parseInt(v.split(':')[1]));

            let mapScaleIdx = divisors.indexOf(mapScale);
            if (mapScaleIdx === -1) {
                divisors.push(mapScale);
                divisors.sort((a, b) => a - b);
                mapScaleIdx = Math.max(0, divisors.indexOf(mapScale) - 1);
            }

            const elt = this.document.querySelector(`nlr-zoom-scale ng-select > ng-dropdown-panel div.ng-option:nth-of-type(${mapScaleIdx + 1})`);
            if (elt) {
                elt.scrollIntoView(true);
            }
        }, 50);
    }
}
